import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Box,
  TableContainer,
  TableHead,
  Table,
  TableRow,
  TableCell,
  TableBody,
  InputLabel,
  Avatar,
  Popover,
  Checkbox,
} from "@mui/material";
import { useFormik } from 'formik';
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import Button from "../../../ui/elements/buttons/Button";
import EditIcon from "@mui/icons-material/Edit";
import profileIcon from "../../../assets/images/profileIcon.png";
import useUpdateExhibitor from '../mutations/useUpdateExhibitor';
import currencyCodes from 'currency-codes';
import 'currency-flags/dist/currency-flags.min.css';

import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from "@mui/material";
import headerBackground from "../../../assets/svgs/headerBackground.svg";
import { Exhibitor, PriceRange } from "../exhibitorType/Exhibitor";
import { useNavigate } from "react-router-dom";
import {Price} from "../exhibitorType/Exhibitor"
import MatrixTable from "./MatrixTable";
import { useCurrency } from "../../settings/queries/useCurrency";

interface FormValues {
  exhibitorType: string | undefined;
  pricingStructure: string;
  amount?: string;
  ranges?: PriceRange[];
  discount: number | null;
  currency: string;
  tax_number: number | string;
  email?: string;
}

interface PopupProps {
  open: boolean;
  handleClose: () => void;
  selectedExhibitor?: Exhibitor | null;
  refetchExhibitors: () => void;
}

const AddExhibitorPopup: React.FC<PopupProps> = ({ open, handleClose,selectedExhibitor,refetchExhibitors }) => {

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<number | null>(null);
  const [updatedRanges, setUpdatedRanges] = useState<any[]>([]);

  const matrixInitialValues = [{ "start": 1, "end": 10, "under_2hrs": 0, "over_2hrs": 0 },
    { "start": 11, "end": 20, "under_2hrs": 0, "over_2hrs": 0 },
    { "start": 21, "end": 30, "under_2hrs": 0, "over_2hrs": 0 },
    { "start": 31, "under_2hrs": 0, "over_2hrs": 0 }]

  const { mutate: UpdateExhibitor }=  useUpdateExhibitor();

  const finalRanges = updatedRanges.length ? updatedRanges : matrixInitialValues;


  const handleSave = async () => {
  formik.setTouched({
    exhibitorType: true,
    pricingStructure: true,
    amount: true,
    currency: true,
    tax_number: true,
    email: true,
  });

  await formik.validateForm();

  if (formik.isValid) {
    formik.handleSubmit(); // still submits the form and triggers onSubmit
  }
};


  const handleEditClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(index);
  };


  // Handle closing the popover
  const handleClosePopover = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };
  const openPopover = Boolean(anchorEl);

  const formik = useFormik<FormValues>({
    enableReinitialize: true, 
     initialValues : {
     exhibitorType: selectedExhibitor?.type || "",
     pricingStructure: selectedExhibitor?.price_model || "",
     amount: selectedExhibitor?.price?.amount || "",
     ranges: Array.isArray(selectedExhibitor?.price?.ranges)
      ? selectedExhibitor?.price?.ranges.map((range: any) => ({
          start: 0,
          end: 0,
          ...range, // Retain other properties if present
        }))
      : matrixInitialValues,
     discount: selectedExhibitor?.discount || null,
     currency:selectedExhibitor?.currency || "",
     tax_number:selectedExhibitor?.tax_number || "",
     email: selectedExhibitor?.email || "",
    },
    validate: (values) => {
      const errors: Partial<FormValues> = {};
      if (!values.email) {
        errors.email = "Email is required.";
      } else if (!values.email.includes("@") || !values.email.endsWith(".com")) {
        errors.email = "Invalid email address.";
      }

        if (!values.amount) {
    errors.amount = "Amount is required.";
  }
    
      return errors;
    },
  onSubmit: async (values) => {
    const requestBody: any = {
      data: {    
          name: selectedExhibitor?.name,
          type: values.exhibitorType,
          price_model: values.pricingStructure,
          price: values.pricingStructure === "matrix" 
          ?  { ranges: finalRanges } 
          : { amount: values.amount },
          discount: values.discount,
          currency: values.currency,
          tax_number:values?.tax_number,
          email: values?.email,
      },
    };
    UpdateExhibitor(
      { 
        exhibitorId: selectedExhibitor?.documentId ?? '', 
        updatedExhibitor: requestBody 
      }, 
      {
        onSuccess: () => {
          navigate("/exhibitors");
          refetchExhibitors();
          handleClose()
        },
        onError: (error) => {
          console.error("Error updating exhibitors:", error.message);
        },
      }
    );
  }

});


const {data: currencyData,isLoading,refetch: currencyRefetch} = useCurrency({});
const currency= currencyData?.data || [];

  return (
    <Dialog
      open={open}
      onClose={()=>{handleClose();formik.resetForm();}}
      key={selectedExhibitor?.documentId}
      maxWidth="lg"
      PaperProps={{ style: { width: "999px" }, elevation: 0 }}
    >
      <DialogTitle
        bgcolor={"#000"}
        display={"flex"}
        alignItems={"center"}
        padding={"0 4px"}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          height: { xs: "110px", sm: "90px" },
          position: "relative",
          backgroundImage: `url(${headerBackground})`,
        }}
      >
        <Box sx={{ display: "flex", gap: "10", alignItems: "center" }}>
          <Box marginRight={"10px"}>
            <Avatar
              alt="Vravis Howard"
              src={profileIcon}
              sx={{
                width: { xs: 34, md: 40 },
                height: { xs: 34, md: 40 },
              }}
            />
          </Box>
          <Box sx={{ display: {  md: "block" } }}>
            <Typography color={"white"} fontWeight={700} sx={{ fontSize: {xs: "14px",  md: "18px" } }} >
              {selectedExhibitor?.name}
            </Typography>
            <Typography sx={{ fontSize: {xs: "12px",  md: "14px" } }} fontWeight={700} color={"white"}>
              {selectedExhibitor?.external_id}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={()=>{handleClose();formik.resetForm();}}
          sx={{
            color: "white",
            position: "absolute",
            top: { xs: "8px", sm: "16px" },
            right: { xs: "8px", sm: "16px" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          "&::-webkit-scrollbar": {
            width: "6px",
            // minHeight:'10px',
            height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(91, 91, 91, 1)",
            borderRadius: "8px",
          },

          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(242, 242, 242, 1)",
          },
        }}
      >
        {/* <SelectedTable /> */}
        <form onSubmit={formik.handleSubmit}>
          <Box>
            <Box sx={{ padding: "0px 32px 32px 32px" }}>
              {/* Exhibitor Type */}
              <Box flexDirection={"column"} display={"flex"}>
                <FormControl component="fieldset" sx={{ mb: "20px" }}>
                  <FormLabel
                    sx={{ color: "black", fontWeight: 500 }}
                    component="legend"
                  >
                    Exhibitor Type
                  </FormLabel>
                  <RadioGroup
                    row
                    name="exhibitorType"
                    value={formik.values.exhibitorType}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value="major"
                      control={
                        <Radio
                          sx={{
                            color: "#000",
                            "&.Mui-checked": {
                              color: "rgba(33, 150, 243, 1)",
                            },
                          }}
                        />
                      }
                      label="Major"
                    />
                    <FormControlLabel
                      value="independent"
                      control={
                        <Radio
                          sx={{
                            color: "#000",
                            "&.Mui-checked": {
                              color: "rgba(33, 150, 243, 1)",
                            },
                          }}
                        />
                      }
                      label="Independent"
                    />
                  </RadioGroup>
                </FormControl>

                <Box sx={{ mb: "30px" }}>
                  <FormControl component="fieldset">
                    <FormLabel
                      sx={{ color: "black", fontWeight: 500 }}
                      component="legend"
                    >
                      Pricing Structure
                    </FormLabel>
                    <RadioGroup
                      row
                      name="pricingStructure"
                      value={formik.values.pricingStructure}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="flat"
                        control={
                          <Radio
                            sx={{
                              color: "#000",
                              "&.Mui-checked": {
                                color: "rgba(33, 150, 243, 1)",
                              },
                            }}
                          />
                        }
                        label="Flat Price"
                      />
                      <FormControlLabel
                        value="fixed"
                        control={
                          <Radio
                            sx={{
                              color: "#000",
                              "&.Mui-checked": {
                                color: "rgba(33, 150, 243, 1)",
                              },
                            }}
                          />
                        }
                        label="Fixed Price"
                      />
                      <FormControlLabel
                        value="matrix"
                        control={
                          <Radio
                            sx={{
                              color: "#000",
                              "&.Mui-checked": {
                                color: "rgba(33, 150, 243, 1)",
                              },
                            }}
                          />
                        }
                        label="Price Matrix"
                      />
                    </RadioGroup>
                  </FormControl>

                  {formik.values.pricingStructure === "matrix" && (
                    <MatrixTable
                    price={
                      selectedExhibitor?.price && typeof selectedExhibitor.price !== "number"
                        ? selectedExhibitor.price
                        : { ranges: formik.values.ranges || [] } // Fallback in case price is undefined or not structured correctly
                    }
                    // onRangesUpdate={handleRangesUpdate} 
                    onEditClick={handleEditClick}
                    setUpdatedRanges={setUpdatedRanges}
                    matrixInitialValues={matrixInitialValues}
                  />
                  )}
                  
                </Box>

                {/* Amount Input */}
                {formik.values.pricingStructure !== "matrix" && (
                  <TextField
                    label="Amount"
                    size="small"
                    name="amount"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    error={formik.touched.amount && Boolean(formik.errors.amount)}
                    helperText={formik.touched.amount && formik.errors.amount}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '_'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { color: "black" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                      mb: 3,
                      width: { xs: "100%", md: "30%", sm: '50%' },
                      color: "black",
                    }}
                  />
                )}

                <Box
                  display={"flex"}
                  gap={3}
                  sx={{
                    flexDirection: {
                      xs: "column",
                      md: formik.values.pricingStructure === "matrix"
                        ? "row"
                        : "row",
                    },
                  }}
                >
                  {/* Left Column */}
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={2}
                    sx={{ width: { xs: "100%", md: "50%" } }}
                  >
                    {/* Discount Percentage */}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "16px", fontWeight: 500 }}
                      >
                        Discount Percentage
                      </Typography>
                      <TextField
                        fullWidth
                        label="Discount value"
                        margin="normal"
                        size="small"
                        name="discount"
                        type="number"
                        value={formik.values.discount}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '_'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = Math.min(Number(e.target.value), 100);
                          formik.setFieldValue("discount", value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          min: 0,
                          max: 100,
                        }}
                        sx={{
                          "& .MuiInputLabel-root": { color: "black" },
                          width: "100%",
                        }}
                      />
                    </Box>

                    {/* Currency Input */}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "16px", fontWeight: 500 }}
                      >
                        Currency Input
                      </Typography>
                      <FormControl
                        fullWidth
                        margin="normal"
                        sx={{
                          "& .MuiInputLabel-root": { color: "black" },
                          minWidth: "250px",
                        }}
                      >
                        <InputLabel shrink>Select currency</InputLabel>
                        <Select
                          value={formik.values.currency}
                          onChange={(event) =>
                            formik.setFieldValue("currency", event.target.value)
                          }
                          label="Select currency"
                          displayEmpty
                          size="small"
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                width: "300px",
                                maxHeight: "380px",
                                overflowY: "auto",
                              },
                            },
                          }}
                        >
                          {currency?.map((currency: any) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              <Box display="flex" alignItems="center">
                                <span
                                  className={`currency-flag currency-flag-${currency?.code.toLowerCase()}`}
                                  style={{
                                    marginRight: "10px",
                                    width: "24px",
                                    height: "18px",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    display: "inline-block",
                                  }}
                                ></span>
                                {currency.code}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Right Column */}
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={2}
                    sx={{ width: { xs: "100%", md: "50%" } }}
                  >
                    {/* Taxation Number */}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "16px", fontWeight: 500 }}
                      >
                        Taxation Number
                      </Typography>
                      <TextField
                        fullWidth
                        label="Taxation Number"
                        margin="normal"
                        size="small"
                        name="taxation"
                        value={formik.values.tax_number}
                        onChange={(e) => {
                          formik.setFieldValue("tax_number", e.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiInputLabel-root": { color: "black" },
                          width: "100%",
                        }}
                      />
                    </Box>

                    {/* Email Field */}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "16px", fontWeight: 500 }}
                      >
                        Email
                      </Typography>
                      <TextField
                        fullWidth
                        label="Email"
                        margin="normal"
                        size="small"
                        name="email"
                        type="email"
                        value={formik.values.email || ""}
                        onChange={(e) => {
                          formik.setFieldValue("email", e.target.value);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiInputLabel-root": { color: "black" },
                          width: "100%",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </form>
      </DialogContent>

      <DialogActions
        sx={{ height:{sm:'70px',md:'70px', xs:'120px'}, backgroundColor: "white", padding: "16px" }}
      >
        <Box
          justifyContent={"space-between"}
          width={"100%"}
          alignItems={"center"}
          display={"flex"}
          gap={1}
          sx={{ flexDirection: {xs: "column",  md: "row", sm:'row' } }}
        >
          <Box
            flexDirection={"row"}
            display={"flex"}
            gap={1}
            alignItems={"center"}
          >
            {/* <Checkbox sx={{ color: "#000" }} />
            <Typography>Requires to pay upfront</Typography> */}
          </Box>
          <Button
            type="button" 
            onClick={handleSave}
            color="white"
            sx={{ fontSize: { xs: "12px", sm: "14px" }, width: "200px" }}
            background="rgba(246, 51, 63, 1)"
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddExhibitorPopup;
